import React, { useEffect, useRef, useState } from "react";
import { LogOut } from "lucide-react";
import { useAuth } from "@talis/auth-js";
import { useMe } from "@/hooks/useMe";

function initialsOf(name: string | null | undefined, email: string | null | undefined): string {
  if (name && name.trim()) {
    const parts = name.trim().split(/\s+/);
    if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
    return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
  }
  if (email) return email.slice(0, 2).toUpperCase();
  return "··";
}

export function UserMenu() {
  const { data } = useMe();
  const { logout } = useAuth();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  // Close on outside click.
  useEffect(() => {
    if (!open) return;
    const onClick = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, [open]);

  const initials = initialsOf(data?.name, data?.email);
  const fullName = data?.name ?? data?.email ?? "Signed in";

  function signOut() {
    // Package logout: broadcasts cross-tab + redirects to /sso/logout
    // (federated logout) for ssoStartPath apps like kraken.
    logout();
  }

  return (
    <div className="relative" ref={ref}>
      <button
        onClick={() => setOpen((v) => !v)}
        aria-label={`Account menu for ${fullName}`}
        title={fullName}
        className="h-9 w-9 rounded-full bg-brand text-white text-xs font-semibold flex items-center justify-center hover:bg-brand-dark focus:outline-none focus:ring-2 focus:ring-brand/40 cursor-pointer"
      >
        {initials}
      </button>
      {open && (
        <div className="absolute right-0 mt-xs w-56 rounded-md border border-border bg-surface shadow-card text-sm overflow-hidden">
          <div className="px-md py-sm border-b border-border">
            <div className="font-medium text-text truncate">{data?.name ?? "—"}</div>
            {data?.email && (
              <div className="text-xs text-textLight truncate">{data.email}</div>
            )}
          </div>
          <button
            onClick={signOut}
            className="w-full text-left flex items-center gap-xs px-md py-sm hover:bg-bg cursor-pointer"
          >
            <LogOut size={14} /> Sign out
          </button>
        </div>
      )}
    </div>
  );
}
